import React, { useEffect, useMemo, useState } from 'react';
import OrderDetailsModal from '../../Orders/Components/OrderDetailsModal';
import { TRIP_CLAIMS_CHANGED } from '../../../Utils/tripClaims';

/**
 * Order Details over an Invoicing list. `open(row)` shows the row's first
 * booking on the Financial tab; ‹ Previous / Next › walk every booking on the
 * list, a group's one by one, without closing. Closing returns to the list as
 * it was, and reloads it (`onChanged`) only when something was written —
 * money, a document, a claim decided — so a selection survives a look.
 *
 * Returns `open` and the `modal` to render.
 */
export default function useBookingDetails(rows, onChanged) {
  const bookings = useMemo(() => rows.flatMap((row) => row.bookings), [rows]);
  const [current, setCurrent] = useState(null);
  const [changed, setChanged] = useState(false);

  // Claims are decided through the shared review path, which announces it.
  useEffect(() => {
    if (!current) return undefined;
    const mark = () => setChanged(true);
    window.addEventListener(TRIP_CLAIMS_CHANGED, mark);
    return () => window.removeEventListener(TRIP_CLAIMS_CHANGED, mark);
  }, [current]);

  const open = (row) => setCurrent(row.bookings[0]);

  const close = () => {
    setCurrent(null);
    if (changed) {
      setChanged(false);
      onChanged();
    }
  };

  const index = current ? bookings.findIndex((b) => b.booking_id === current.booking_id) : -1;
  const step = (offset) => {
    const next = bookings[index + offset];
    return index >= 0 && next ? () => setCurrent(next) : null;
  };

  // What the list knows of the booking, for the header until Details has
  // read the booking itself.
  const modal = current && (
    <OrderDetailsModal
      order={{
        _id: current.booking_id,
        booking_reference: current.booking_reference,
        booking_type: current.booking_type,
        status: current.status,
        date_datetime: current.date,
      }}
      show
      handleClose={close}
      initialTab="financial"
      onChanged={() => setChanged(true)}
      siblings={{
        onPrevious: step(-1),
        onNext: step(1),
        position: index >= 0 ? `${index + 1} of ${bookings.length}` : '',
      }}
    />
  );

  return { open, modal };
}
